import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { SearchInput } from '@/components/SearchInput'
import { ContactsList } from '@/components/ContactsList'
import { FavoritesList } from '@/components/FavoritesList'
import { CommunityBanners } from '@/components/CommunityBanners'
import { Avatar } from '@/components/Avatar'
import { SliderItem } from '@/features/dashboard/components/HomePageSliders/components/SliderItem'
// import TgOffsetTop from '@/components/layout/TgOffsetTop'
import { useTheme } from '@/hooks/useTheme'
import { getAvatarProps } from '@/utils/avatarUtils'
import { useCommunityStore, useUserStore } from '@/store'
import { useGetFeaturesSettings, useGetWallet } from '@/domain'
import { homeSliders, HomeSlidersEnumID } from '@/constants/homeSliders'
import { BoxoBanners } from '@/features/boxo/components/BoxoBanners'
import type { UsersAndContacts } from '@/types/contacts'
import { useFavoriteUsers } from '@/hooks/useContacts'
import { extractPhoneNumberInfosWithoutRegionCode } from '@/utils/phoneNumber/phoneNumberUtils'

// Mock contacts data for Contacts section
const _mockContacts: UsersAndContacts[] = [
  {
    id: 'mock-1',
    username: '',
    first_name: 'Alice',
    last_name: 'Johnson',
    middle_name: '',
    phone_number: '+12345678900',
    avatar_path: '',
    avatarBg: ['#FF6B6B', '#4ECDC4', '#45B7D1'],
    is_blocked: false,
    is_favorite: false,
  },
  {
    id: 'mock-2',
    username: '',
    first_name: 'Bob',
    last_name: 'Smith',
    middle_name: '',
    phone_number: '19876543210',
    avatar_path: '',
    avatarBg: ['#96CEB4', '#FFEAA7', '#DDA0DD'],
    is_blocked: false,
    is_favorite: true,
  },
  {
    id: 'mock-3',
    username: '',
    first_name: 'Charlie',
    last_name: 'Brown',
    middle_name: '',
    phone_number: '15551234567',
    avatar_path: '',
    avatarBg: ['#74B9FF', '#FD79A8', '#FDCB6E'],
    is_blocked: false,
    is_favorite: false,
  },
  {
    id: 'mock-4',
    username: '',
    first_name: 'David',
    last_name: 'Wilson',
    middle_name: '',
    phone_number: '15551111111',
    avatar_path: '',
    avatarBg: ['#FF6B6B', '#4ECDC4', '#45B7D1'],
    is_blocked: false,
    is_favorite: false,
  },
  {
    id: 'mock-5',
    username: '',
    first_name: 'Emma',
    last_name: 'Davis',
    middle_name: '',
    phone_number: '15552222222',
    avatar_path: '',
    avatarBg: ['#96CEB4', '#FFEAA7', '#DDA0DD'],
    is_blocked: false,
    is_favorite: false,
  },
]

// Component for rendering a contact item in Contacts section
const _ContactsBlockItem: React.FC<{
  contact: UsersAndContacts
  onPress: (contact: UsersAndContacts) => void
}> = ({ contact, onPress }) => {
  const avatarProps = getAvatarProps({
    first_name: contact.first_name,
    last_name: contact.last_name,
    avatar_path: contact.avatar_path,
    avatarBg: contact.avatarBg,
    id: contact.id,
  })

  const displayName = contact.first_name || contact.username || 'Unknown'
  const subtitle = contact.username
    ? `@${contact.username}`
    : contact.phone_number

  const handlePress = () => {
    onPress(contact)
  }

  return (
    <BaseView
      data-component="contacts-list-item"
      bgColor="background.cardItemRoot"
      row
      align="center"
      gap={12}
      className="mb-2 cursor-pointer hover:opacity-90 active:scale-[0.99] transition-all duration-150 ease-out"
      style={{
        padding: '12px',
        borderRadius: '24px',
        minHeight: '72px',
      }}
      onClick={handlePress}
    >
      {/* Avatar */}
      <Avatar
        size={62}
        initials={avatarProps.initials}
        imageUrl={avatarProps.avatarUrl}
        gradientColors={avatarProps.backgroundGradient}
        fullSize={false}
      />

      {/* Contact Info */}
      <BaseView
        className="flex-1 min-w-0"
        style={{
          gap: '6px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          flex: 1,
        }}
      >
        {/* Name */}
        <BaseText
          className="truncate"
          fontSize={16}
          fontFamily="medium"
          color="label.textTextInput"
          style={{
            lineHeight: '20px',
            margin: 0,
            width: '100%',
          }}
        >
          {displayName}
        </BaseText>

        {/* Phone Number */}
        {subtitle && (
          <BaseText
            className="truncate"
            fontSize={16}
            fontFamily="medium"
            color={contact.username ? 'label.primary' : 'label.description'}
            style={{
              lineHeight: '20px',
              margin: 0,
              width: '100%',
            }}
          >
            {subtitle}
          </BaseText>
        )}
      </BaseView>

      {/* Chevron */}
      <BaseView
        style={{
          justifyContent: 'center',
          alignItems: 'flex-end',
          maxWidth: '150px',
          flexShrink: 0,
        }}
      >
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{
            color: 'rgba(255, 255, 255, 0.6)',
            flexShrink: 0,
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </BaseView>
    </BaseView>
  )
}

export const CommunityScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const _theme = useTheme()

  // Track if search input is focused
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  // Check if user has issued a card
  const { data: walletData, isLoading: isLoadingWallet } = useGetWallet()
  const userAuthenticated = useUserStore((state) => state.userAuthenticated)

  // Determine if user has cards - only true when data is loaded AND cards exist
  // While loading, treat as "has cards" to prevent showing the banner prematurely
  const hasCards = isLoadingWallet
    ? true // Hide banner while loading
    : Boolean(walletData?.cards?.length && walletData?.cards?.length > 0)

  // Find the cashback banner from homeSliders
  const cashbackBanner = homeSliders.find(
    (slider) => slider.id === HomeSlidersEnumID.cashback,
  )

  // Use global store instead of local state
  const searchQuery = useCommunityStore((state) => state.searchQuery)
  const immediateSearchValue = useCommunityStore(
    (state) => state.immediateSearchValue,
  )
  const setSearchQuery = useCommunityStore((state) => state.setSearchQuery)
  const setImmediateSearchValue = useCommunityStore(
    (state) => state.setImmediateSearchValue,
  )
  const clearSearch = useCommunityStore((state) => state.clearSearch)

  const handleContactSelected = useCallback(
    (contact: UsersAndContacts) => {
      if (contact.username) {
        navigate(`/user-profile/${contact.id}`)
      } else {
        navigate('/profile-non-user', {
          state: {
            id: contact.id,
            firstName: contact.first_name,
            lastName: contact.last_name,
            phoneNumber: contact.phone_number || '',
            avatarUrl: contact.avatar_path,
            avatarBg: contact.avatarBg,
          },
        })
      }
    },
    [navigate],
  )

  // Immediate change handler (like React Native onChangeText)
  const handleImmediateSearchChange = useCallback(
    (text: string) => {
      setImmediateSearchValue(text)
    },
    [setImmediateSearchValue],
  )

  // Debounced change handler (like React Native onChangeTextDebounced)
  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchQuery(text)
    },
    [setSearchQuery],
  )

  // Clear handler for both states
  const handleClear = useCallback(() => {
    clearSearch()
  }, [clearSearch])

  // Focus handlers for SearchInput
  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true)
  }, [])

  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false)
  }, [])

  const searchRef = useRef<HTMLInputElement>(null)

  const { data: featureSettings } = useGetFeaturesSettings()
  const rewardsAllowedCountries = featureSettings?.rewards?.allowed_countries
  const rewardsLimit = featureSettings?.reward_limit_formatted
  const rewardsEnabled = featureSettings?.rewards?.enabled
  const cashbackInfo = featureSettings?.cashback?.cashback_programs?.[0]
  const userPhoneNumber = userAuthenticated?.phone_number
  const userPhoneNumberInfos = userPhoneNumber
    ? extractPhoneNumberInfosWithoutRegionCode(userPhoneNumber)
    : null
  const isRewardsAllowedCountryByPhone = userPhoneNumberInfos?.iso3Code
    ? rewardsAllowedCountries?.includes(userPhoneNumberInfos?.iso3Code || '')
    : false

  const { data: favorites = [], isLoading: isLoadingFavorites } =
    useFavoriteUsers()
  return (
    <>
      {/* Sticky Header with Telegram Offsets */}

      <BaseView data-component="header-navigation-community" px={12} py={16}>
        <BaseText fontSize={24} fontFamily="semibold" color="label.default">
          {t('common:community')}
        </BaseText>

        {/* Search */}
        <BaseView gap={20} pt={12}>
          <SearchInput
            placeholder={t('form:placeholders.search')}
            withoutBorder
            defaultValue={immediateSearchValue}
            onChangeText={handleImmediateSearchChange}
            onChangeTextDebounced={handleSearchChange}
            clean={handleClear}
            debounceDelay={200}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            ref={searchRef}
          />
        </BaseView>
      </BaseView>

      {/* Scrollable Content Area */}
      <BaseView
        px={13}
        style={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <div
          className="smooth-scroll"
          style={{
            height: '100%',
            overflow: 'auto',
            // padding: '14px 12px',
            paddingTop: '0px',
            paddingBottom: '100px', // Sufficient bottom padding to avoid jitter
            overscrollBehavior: 'none', // Prevents bounce effect and jitter
          }}
        >
          {/* Community Banners - only when not searching and not focused */}
          {/* {!immediateSearchValue && !isSearchFocused && (
            <BaseView style={{ marginBottom: '20px' }}>
              <CommunityBanners
                hasSearch={!!immediateSearchValue}
                shouldDisplayReferralBonuses={true}
              />
            </BaseView>
          )} */}

          {/* Cashback Banner - only when not searching, not focused, and user doesn't have a card */}
          {!immediateSearchValue &&
            !isSearchFocused &&
            !hasCards &&
            cashbackBanner &&
            cashbackInfo && (
              <BaseView style={{ marginBottom: '20px', paddingLeft: '0px' }}>
                <SliderItem
                  id={cashbackBanner.id}
                  title={t(cashbackBanner.title, {
                    percentage: `${cashbackInfo.percentage}%`,
                    cryptoName: cashbackInfo.name,
                  })}
                  description={
                    cashbackBanner.description
                      ? t(cashbackBanner.description, {
                          percentage: `${cashbackInfo.percentage}%`,
                        })
                      : undefined
                  }
                  descriptionFontSize={cashbackBanner.descriptionFontSize}
                  backgroundImage={cashbackBanner.backgroundImage}
                  internalImage={cashbackBanner.internalImage}
                  descriptionIconName={cashbackBanner.descriptionIconName}
                  maxContentWidth={cashbackBanner.maxContentWidth}
                  onPress={() => navigate('/cashback-info')}
                />
              </BaseView>
            )}

          {/* Favorites List (Pinned) - only when not searching */}
          {!immediateSearchValue && (
            <FavoritesList
              onContactSelected={handleContactSelected}
              className=""
              ref={searchRef}
              favorites={favorites}
              isLoading={isLoadingFavorites}
            />
          )}

          {/* Boxo Banners - only when not searching and not focused */}
          {!immediateSearchValue && !isSearchFocused && <BoxoBanners />}

          {!immediateSearchValue &&
            !isSearchFocused &&
            rewardsEnabled &&
            isRewardsAllowedCountryByPhone && (
              <BaseView style={{ marginTop: '20px', paddingLeft: '0px' }}>
                <SliderItem
                  id={HomeSlidersEnumID.inviteFriend}
                  title={t('banners:inviteFriends.title', {
                    formattedAmount: rewardsLimit,
                  })}
                  titleFontSize={18}
                  description={''}
                  descriptionFontSize={14}
                  backgroundImage={
                    '/assets/images/homeBanners/invite-friends-banner-bg.png'
                  }
                  internalImage={''}
                  descriptionIconName={''}
                  maxContentWidth={180}
                  onPress={() => navigate('/invite-friends')}
                />
              </BaseView>
            )}

          {/* Contacts Block - only when not searching */}
          {/* {!immediateSearchValue && (
            <BaseView className="flex-1" style={{ gap: '16px' }}>
              <BaseView>
                <BaseText
                  fontSize={18}
                  fontFamily="semibold"
                  color="label.textTextInput"
                  style={{
                    margin: 0,
                    marginTop: '20px',
                  }}
                >
                  {t('common:contacts')}
                </BaseText>
              </BaseView>

              <BaseView className="flex-1 gap-1">
                {mockContacts.map((contact, index) => (
                  <ContactsBlockItem
                    key={`mock-contact-${contact.id}-${index}`}
                    contact={contact}
                    onPress={handleContactSelected}
                  />
                ))}
              </BaseView>
            </BaseView>
          )} */}

          {/* Contacts List - only when searching */}
          {immediateSearchValue && (
            <BaseView flex={1}>
              <ContactsList
                search={searchQuery}
                onContactSelected={handleContactSelected}
                isEnabledTransferToNonUsers={true}
                transferType="send"
                className=""
              />
            </BaseView>
          )}
        </div>
      </BaseView>
    </>
  )
}
